import { t } from "i18next";
import { appStore } from "src/appStore";

import { refresh } from "./apiAuth";
import { ApiResponseWithStatus } from "./types";

let authWait: Promise<ApiResponseWithStatus> | null = null;

const getResponseStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return undefined;
  }
  const { response } = error as { response?: unknown };
  if (!response || typeof response !== "object" || !("status" in response)) {
    return undefined;
  }
  const { status } = response as { status?: unknown };

  return typeof status === "number" ? status : undefined;
};

const failAuthorization = (): never => {
  const error = new Error(t("Authorization required"));
  if (appStore.userData.status !== "error") {
    appStore.setUserData({ status: "error", error });
  }
  appStore.setOverlayType("login");
  throw error;
};

export const authTask = async <R extends ApiResponseWithStatus>(
  task: () => Promise<R>,
): Promise<R> => {
  let resp: R;
  try {
    resp = await task();
  } catch (e: unknown) {
    if (e && typeof e === "object" && "response" in e) {
      const error = e as { response: R };
      resp = error.response;
      if (resp.status !== 401) {
        throw e;
      }
    } else {
      throw e;
    }
  }

  if (resp.status === 401) {
    try {
      authWait = authWait || refresh();
      const resp1 = await authWait;
      if (resp1.status === 401) {
        failAuthorization();
      }
      if (resp1.status !== 200) {
        throw new Error(`Refresh failed with status ${resp1.status}`);
      }
    } catch (error: unknown) {
      if (getResponseStatus(error) === 401) {
        failAuthorization();
      }
      throw error;
    } finally {
      authWait = null;
    }
    resp = await task();
  }
  return resp;
};
